import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  ChevronRight,
  Download,
  Eye,
  FileSpreadsheet,
  ListFilter,
  LoaderCircle,
  MoreHorizontal,
  Pencil,
  Plus,
  Settings2,
  Trash2,
  Workflow,
  X,
} from "lucide-react";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
  ConfirmDialog,
  EmptyState,
  FeedbackBanner,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import {
  downloadFile,
  WorkflowRecordDownloadDialog,
  WorkflowRecordTextDialog,
} from "../components/WorkflowRecordExportDialogs.jsx";

const RECORDS_PAGE_SIZE = 25;
const RECORD_ACTIONS_COLUMN_WIDTH = 94;
const SYSTEM_FILTER_FIELDS = [
  {
    id: "system_created_at",
    label: "Fecha de creación",
    type: "date",
  },
  {
    id: "system_updated_at",
    label: "Fecha de modificación",
    type: "date",
  },
];
const numberFormatter = new Intl.NumberFormat("es-AR", {
  maximumFractionDigits: 8,
});
const moneyFormatter = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 2,
});
const dateFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
});
const argentinaCalendarFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Argentina/Buenos_Aires",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function calendarDate(year, month, day) {
  return [year, month, day]
    .map((part, index) => (
      index === 0 ? String(part) : String(part).padStart(2, "0")
    ))
    .join("-");
}

function argentinaCalendarParts(value = new Date()) {
  const parts = Object.fromEntries(
    argentinaCalendarFormatter.formatToParts(value)
      .filter(({ type }) => ["year", "month", "day"].includes(type))
      .map(({ type, value: partValue }) => [type, Number(partValue)]),
  );
  return parts;
}

function monthRange(year, month) {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    from: calendarDate(year, month, 1),
    to: calendarDate(year, month, lastDay),
  };
}

function dateFilterPresets(referenceDate = new Date()) {
  const { year, month } = argentinaCalendarParts(referenceDate);
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousMonthYear = month === 1 ? year - 1 : year;
  return [
    {
      id: "this_month",
      label: "Este mes",
      ...monthRange(year, month),
    },
    {
      id: "last_month",
      label: "Mes pasado",
      ...monthRange(previousMonthYear, previousMonth),
    },
    {
      id: "this_year",
      label: "Este año",
      from: calendarDate(year, 1, 1),
      to: calendarDate(year, 12, 31),
    },
    {
      id: "last_year",
      label: "Año pasado",
      from: calendarDate(year - 1, 1, 1),
      to: calendarDate(year - 1, 12, 31),
    },
  ];
}

function defaultWorkflowRecordFilters(referenceDate = new Date()) {
  const { from, to } = dateFilterPresets(referenceDate)[0];
  return {
    system_created_at: { type: "date", from, to },
  };
}

function formatDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "—";
  const [, year, month, day] = match;
  return dateFormatter.format(
    new Date(Number(year), Number(month) - 1, Number(day), 12),
  );
}

function formatValue(field, value) {
  if (field.type === "boolean") return value ? "Sí" : "No";
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "judicial_interest") {
    return value?.status === "calculated"
      ? moneyFormatter.format(Number(value.summary?.total || 0))
      : "Cálculo pendiente";
  }
  if (field.type === "computed_percentage") {
    return Number.isFinite(Number(value?.amount))
      ? moneyFormatter.format(Number(value.amount))
      : "Pendiente";
  }
  if (field.type === "legal_contribution") {
    return Number.isFinite(Number(value?.balance))
      ? moneyFormatter.format(Number(value.balance))
      : "Pendiente";
  }
  if (field.type === "select") {
    return field.options?.find(({ id, label }) => (
      id === value
      || label.toLocaleLowerCase("es-AR")
        === String(value).toLocaleLowerCase("es-AR")
    ))?.label || String(value);
  }
  if (field.type === "number") return numberFormatter.format(value);
  if (["date", "computed_date"].includes(field.type)) return formatDate(value);
  return String(value);
}

function columnWidth(field, index, primaryWidth = 154) {
  if (index === 0) return primaryWidth;
  if (field.type === "text") return 180;
  if (field.type === "boolean") return 64;
  if (["date", "computed_date"].includes(field.type)) return 86;
  if (field.type === "select") return 112;
  if (field.type === "judicial_interest") return 125;
  if (["computed_percentage", "legal_contribution"].includes(field.type)) {
    return 115;
  }
  if (field.type === "number") return 92;
  return 100;
}

let workflowRecordMeasurementContext;

function measuredTextWidth(value, font) {
  if (typeof document === "undefined") {
    return String(value || "").length * 8;
  }
  if (!workflowRecordMeasurementContext) {
    workflowRecordMeasurementContext = document
      .createElement("canvas")
      .getContext("2d");
  }
  if (!workflowRecordMeasurementContext) {
    return String(value || "").length * 8;
  }
  workflowRecordMeasurementContext.font = font;
  return workflowRecordMeasurementContext.measureText(String(value || "")).width;
}

function primaryColumnWidth(template, records) {
  const field = template?.fields?.[0];
  if (!field) return 154;
  const headerWidth = measuredTextWidth(
    String(field.label || "").toLocaleUpperCase("es-AR"),
    '700 10px "Geist", "Inter", system-ui, sans-serif',
  );
  const valueWidth = records.reduce((widest, record) => Math.max(
    widest,
    measuredTextWidth(
      formatValue(field, record.values[field.id]),
      '700 12px "Geist", "Inter", system-ui, sans-serif',
    ),
  ), 0);
  return Math.ceil(Math.max(154, headerWidth, valueWidth) + 16);
}

function filterType(field) {
  return field.type === "computed_date" ? "date" : field.type;
}

function emptyFilterFor(field) {
  const type = filterType(field);
  if (type === "text") return { type, query: "" };
  if (type === "number") {
    return { type, greaterThan: "", lessThan: "" };
  }
  if (type === "select") return { type, values: [] };
  return { type: "date", from: "", to: "" };
}

function hasFilterValue(filter) {
  if (filter.type === "text") return Boolean(filter.query.trim());
  if (filter.type === "number") {
    return filter.greaterThan !== "" || filter.lessThan !== "";
  }
  if (filter.type === "select") return filter.values.length > 0;
  return Boolean(filter.from || filter.to);
}

function cloneFilters(filters) {
  return Object.fromEntries(Object.entries(filters).map(([fieldId, filter]) => [
    fieldId,
    {
      ...filter,
      ...(filter.type === "select" ? { values: [...filter.values] } : {}),
    },
  ]));
}

function shiftOneYear(value, direction) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "";
  const [, rawYear, rawMonth, rawDay] = match;
  const year = Number(rawYear) + direction;
  const month = Number(rawMonth);
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return [
    year,
    rawMonth,
    String(Math.min(Number(rawDay), lastDay)).padStart(2, "0"),
  ].join("-");
}

function WorkflowRecordActions({
  recordLabel,
  onView,
  onEdit,
  onDelete,
  onDownload,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    const handlePointerDown = (event) => {
      if (
        triggerRef.current?.contains(event.target)
        || menuRef.current?.contains(event.target)
      ) return;
      close();
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menuOpen]);

  const toggleMenu = (event) => {
    event.stopPropagation();
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 178;
    const menuHeight = 166;
    const spaceBelow = window.innerHeight - rect.bottom;
    setMenuPosition({
      left: Math.max(8, Math.min(
        window.innerWidth - menuWidth - 8,
        rect.right - menuWidth,
      )),
      top: spaceBelow >= menuHeight + 8
        ? rect.bottom + 6
        : Math.max(8, rect.top - menuHeight - 6),
    });
    setMenuOpen(true);
  };

  const runAction = (action) => {
    setMenuOpen(false);
    action();
  };

  return (
    <div
      className="workflow-record-row-actions"
      role="group"
      aria-label={`Acciones de ${recordLabel}`}
    >
      <button
        className="workflow-record-view-button"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onView();
        }}
      >
        Ver
      </button>
      <button
        ref={triggerRef}
        className="workflow-record-more-button"
        type="button"
        aria-label={`Más acciones de ${recordLabel}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={toggleMenu}
      >
        <MoreHorizontal size={17} aria-hidden="true" />
      </button>
      {menuOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          className="workflow-record-action-menu"
          role="menu"
          aria-label={`Acciones de ${recordLabel}`}
          style={menuPosition}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" role="menuitem" onClick={() => runAction(onView)}>
            <Eye size={15} aria-hidden="true" />
            Ver
          </button>
          <button type="button" role="menuitem" onClick={() => runAction(onEdit)}>
            <Pencil size={15} aria-hidden="true" />
            Editar
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => runAction(onDownload)}
          >
            <Download size={15} aria-hidden="true" />
            Descargar
          </button>
          <button
            className="is-danger"
            type="button"
            role="menuitem"
            onClick={() => runAction(onDelete)}
          >
            <Trash2 size={15} aria-hidden="true" />
            Eliminar
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

function apiFilters(filters) {
  return Object.entries(filters).filter(([, filter]) => (
    hasFilterValue(filter)
  )).map(([fieldId, filter]) => {
    if (filter.type === "text") {
      return { fieldId, type: "text", query: filter.query.trim() };
    }
    if (filter.type === "number") {
      return {
        fieldId,
        type: "number",
        ...(filter.greaterThan !== ""
          ? { greaterThan: Number(filter.greaterThan) }
          : {}),
        ...(filter.lessThan !== ""
          ? { lessThan: Number(filter.lessThan) }
          : {}),
      };
    }
    if (filter.type === "select") {
      return { fieldId, type: "select", values: filter.values };
    }
    return {
      fieldId,
      type: "date",
      ...(filter.from ? { from: filter.from } : {}),
      ...(filter.to ? { to: filter.to } : {}),
    };
  });
}

export function WorkflowRecordsPage({ templateId }) {
  const api = useApi();
  const { navigate } = useRouter();
  const [template, setTemplate] = useState(null);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: RECORDS_PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [templateLoading, setTemplateLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [templateError, setTemplateError] = useState("");
  const [recordsError, setRecordsError] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(
    defaultWorkflowRecordFilters,
  );
  const [appliedFilters, setAppliedFilters] = useState(
    defaultWorkflowRecordFilters,
  );
  const [filterError, setFilterError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [downloadTarget, setDownloadTarget] = useState(null);
  const [preparingFormat, setPreparingFormat] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [textPreview, setTextPreview] = useState(null);

  useEffect(() => {
    let active = true;
    setTemplateLoading(true);
    setTemplateError("");
    api.getWorkflowTemplate(templateId)
      .then((templateResult) => {
        if (!active) return;
        setTemplate(templateResult);
      })
      .catch((requestError) => {
        if (active) setTemplateError(requestError.message);
      })
      .finally(() => {
        if (active) setTemplateLoading(false);
      });
    return () => { active = false; };
  }, [api, templateId]);

  useEffect(() => {
    const defaultFilters = defaultWorkflowRecordFilters();
    setPage(1);
    setDraftFilters(cloneFilters(defaultFilters));
    setAppliedFilters(cloneFilters(defaultFilters));
    setFiltersOpen(false);
  }, [templateId]);

  useEffect(() => {
    let active = true;
    setRecordsLoading(true);
    setRecordsError("");
    api.listWorkflowRecords(templateId, {
      page,
      limit: RECORDS_PAGE_SIZE,
      filters: apiFilters(appliedFilters),
    })
      .then((result) => {
        if (!active) return;
        setRecords(result.items);
        setPagination(result.pagination);
      })
      .catch((requestError) => {
        if (active) setRecordsError(requestError.message);
      })
      .finally(() => {
        if (active) setRecordsLoading(false);
      });
    return () => { active = false; };
  }, [api, appliedFilters, page, templateId]);

  const filterableFields = [
    ...(template?.fields || []).filter((field) => (
      ["text", "number", "select", "date", "computed_date"]
        .includes(field.type)
    )),
    ...SYSTEM_FILTER_FIELDS,
  ];
  const availableFilterFields = filterableFields.filter((field) => (
    !draftFilters[field.id]
  ));
  const appliedFilterCount = Object.keys(appliedFilters).length;
  const quickDateFilters = dateFilterPresets();
  const primaryFieldWidth = primaryColumnWidth(template, records);
  const deleteTargetLabel = deleteTarget && template?.fields?.[0]
    ? formatValue(
        template.fields[0],
        deleteTarget.values[template.fields[0].id],
      )
    : "Este registro";
  const downloadTargetLabel = downloadTarget && template?.fields?.[0]
    ? formatValue(
        template.fields[0],
        downloadTarget.values[template.fields[0].id],
      )
    : "Este registro";

  const updateDraftFilter = (fieldId, changes) => {
    setDraftFilters((current) => ({
      ...current,
      [fieldId]: { ...current[fieldId], ...changes },
    }));
  };

  const openFilters = () => {
    setDraftFilters(cloneFilters(appliedFilters));
    setFilterError("");
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    const prepared = apiFilters(draftFilters);
    const invalidRange = prepared.find((filter) => (
      (filter.type === "number"
        && filter.greaterThan !== undefined
        && filter.lessThan !== undefined
        && filter.greaterThan >= filter.lessThan)
      || (filter.type === "date"
        && filter.from
        && filter.to
        && filter.from > filter.to)
    ));
    if (invalidRange) {
      setFilterError(
        "El valor inicial de un rango debe ser menor que el valor final.",
      );
      return;
    }
    const creationRange = prepared.find(({ fieldId }) => (
      fieldId === "system_created_at"
    ));
    if (creationRange && (!creationRange.from || !creationRange.to)) {
      setFilterError(
        "Fecha de creación necesita una fecha desde y una fecha hasta.",
      );
      return;
    }
    if (
      creationRange
      && creationRange.to > shiftOneYear(creationRange.from, 1)
    ) {
      setFilterError("El rango de Fecha de creación no puede superar un año.");
      return;
    }
    const nextFilters = Object.fromEntries(
      Object.entries(draftFilters).filter(([, filter]) => (
        hasFilterValue(filter)
      )),
    );
    setAppliedFilters(cloneFilters(nextFilters));
    setPage(1);
    setFiltersOpen(false);
    setFilterError("");
  };

  const clearFilters = () => {
    setDraftFilters({});
    setAppliedFilters({});
    setPage(1);
    setFiltersOpen(false);
    setFilterError("");
  };

  const editRecord = (recordId) => navigate(
    `/flujos/${templateId}/registros/${recordId}`,
  );

  const viewRecord = (recordId) => navigate(
    `/flujos/${templateId}/registros/${recordId}/ver`,
  );

  const deleteRecord = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.deleteWorkflowRecord(templateId, deleteTarget.id);
      setDeleteTarget(null);
      if (records.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        setRecords((current) => current.filter(
          ({ id }) => id !== deleteTarget.id,
        ));
        setPagination((current) => {
          const total = Math.max(0, current.total - 1);
          return {
            ...current,
            total,
            pages: Math.max(1, Math.ceil(total / current.limit)),
          };
        });
      }
    } catch (requestError) {
      setDeleteError(requestError.message);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const downloadRecordWord = async () => {
    if (!downloadTarget || preparingFormat) return;
    setPreparingFormat("word");
    setDownloadError("");
    try {
      downloadFile(await api.exportWorkflowRecordWord(
        templateId,
        downloadTarget.id,
      ));
      setDownloadTarget(null);
    } catch (requestError) {
      setDownloadError(requestError.message);
    } finally {
      setPreparingFormat("");
    }
  };

  const downloadRecordPdf = async () => {
    if (!downloadTarget || preparingFormat) return;
    setPreparingFormat("pdf");
    setDownloadError("");
    try {
      downloadFile(await api.exportWorkflowRecordPdf(
        templateId,
        downloadTarget.id,
      ));
      setDownloadTarget(null);
    } catch (requestError) {
      setDownloadError(requestError.message);
    } finally {
      setPreparingFormat("");
    }
  };

  const viewRecordText = async () => {
    if (!downloadTarget || preparingFormat) return;
    setPreparingFormat("text");
    setDownloadError("");
    const recordLabel = downloadTargetLabel;
    try {
      const result = await api.getWorkflowRecordText(
        templateId,
        downloadTarget.id,
      );
      setDownloadTarget(null);
      setTextPreview({ recordLabel, text: result.text });
    } catch (requestError) {
      setDownloadError(requestError.message);
    } finally {
      setPreparingFormat("");
    }
  };

  const exportRecords = async () => {
    setExporting(true);
    setExportError("");
    try {
      downloadFile(await api.exportWorkflowRecords(
        templateId,
        apiFilters(appliedFilters),
      ));
    } catch (requestError) {
      setExportError(requestError.message);
    } finally {
      setExporting(false);
    }
  };

  if (templateLoading) {
    return (
      <div className="page workflow-records-page">
        <div className="workflow-templates-loading">
          <LoaderCircle className="spin" size={23} />
          <span>Cargando registros…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page workflow-records-page">
      <PageHeader
        hero
        eyebrow="Praxis · Flujos"
        title={template?.name || "Registros"}
        description={template?.description
          || "Cargá y consultá la información definida en esta plantilla."}
        actions={(
          <div className="workflow-record-header-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => navigate("/flujos")}
            >
              <ArrowLeft size={16} />
              Flujos
            </button>
            {template?.permissions?.canManage && (
              <button
                className="button secondary"
                type="button"
                onClick={() => navigate(
                  `/flujos/${templateId}/configuracion`,
                )}
              >
                <Settings2 size={16} />
                Configurar
              </button>
            )}
            {filterableFields.length > 0 && (
              <button
                className="button secondary"
                type="button"
                onClick={openFilters}
              >
                <ListFilter size={16} />
                Filtros
                {appliedFilterCount > 0 && (
                  <span className="workflow-record-filter-count">
                    {appliedFilterCount}
                  </span>
                )}
              </button>
            )}
            <button
              className="button secondary"
              type="button"
              disabled={!template || exporting}
              onClick={exportRecords}
            >
              {exporting
                ? <LoaderCircle className="spin" size={16} />
                : <FileSpreadsheet size={16} />}
              {exporting ? "Exportando…" : "Exportar Excel"}
            </button>
            <button
              className="button primary"
              type="button"
              disabled={!template}
              onClick={() => navigate(
                `/flujos/${templateId}/registros/nuevo`,
              )}
            >
              <Plus size={17} />
              Nuevo registro
            </button>
          </div>
        )}
      />

      {templateError && (
        <FeedbackBanner tone="error">{templateError}</FeedbackBanner>
      )}
      {recordsError && (
        <FeedbackBanner tone="error">{recordsError}</FeedbackBanner>
      )}
      {exportError && (
        <FeedbackBanner tone="error">{exportError}</FeedbackBanner>
      )}
      {deleteError && (
        <FeedbackBanner tone="error">{deleteError}</FeedbackBanner>
      )}

      {filtersOpen && (
        <section className="workflow-record-filters" aria-label="Filtros">
          <header>
            <div>
              <ListFilter size={18} />
              <span>
                <strong>Filtrar registros</strong>
                <small>Elegí las columnas que querés consultar.</small>
              </span>
            </div>
            <button
              type="button"
              aria-label="Cerrar filtros"
              onClick={() => setFiltersOpen(false)}
            >
              <X size={17} />
            </button>
          </header>

          {availableFilterFields.length > 0 && (
            <label className="workflow-record-filter-add">
              <span>Agregar filtro</span>
              <select
                value=""
                onChange={(event) => {
                  const field = filterableFields.find(
                    ({ id }) => id === event.target.value,
                  );
                  if (!field) return;
                  setDraftFilters((current) => ({
                    ...current,
                    [field.id]: emptyFilterFor(field),
                  }));
                }}
              >
                <option value="">Seleccioná una columna…</option>
                {availableFilterFields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {Object.keys(draftFilters).length === 0 && (
            <p className="workflow-record-filter-empty">
              Agregá una columna para definir el primer filtro.
            </p>
          )}

          <div className="workflow-record-filter-list">
            {Object.entries(draftFilters).map(([fieldId, filter]) => {
              const field = filterableFields.find(({ id }) => id === fieldId);
              if (!field) return null;
              return (
                <article key={fieldId} className="workflow-record-filter-item">
                  <header>
                    <strong>{field.label}</strong>
                    <button
                      type="button"
                      aria-label={`Quitar filtro ${field.label}`}
                      onClick={() => setDraftFilters((current) => {
                        const next = { ...current };
                        delete next[fieldId];
                        return next;
                      })}
                    >
                      <X size={15} />
                    </button>
                  </header>

                  {filter.type === "text" && (
                    <input
                      type="search"
                      value={filter.query}
                      placeholder={`Buscar en ${field.label}`}
                      onChange={(event) => updateDraftFilter(fieldId, {
                        query: event.target.value,
                      })}
                    />
                  )}

                  {filter.type === "number" && (
                    <div className="workflow-record-filter-range">
                      <label>
                        <span>Mayor que</span>
                        <input
                          type="number"
                          value={filter.greaterThan}
                          onChange={(event) => updateDraftFilter(fieldId, {
                            greaterThan: event.target.value,
                          })}
                        />
                      </label>
                      <label>
                        <span>Menor que</span>
                        <input
                          type="number"
                          value={filter.lessThan}
                          onChange={(event) => updateDraftFilter(fieldId, {
                            lessThan: event.target.value,
                          })}
                        />
                      </label>
                    </div>
                  )}

                  {filter.type === "date" && (
                    <div className="workflow-record-filter-range">
                      <div
                        className="workflow-record-filter-presets"
                        role="group"
                        aria-label={`Rangos rápidos para ${field.label}`}
                      >
                        {quickDateFilters.map((preset) => {
                          const active = filter.from === preset.from
                            && filter.to === preset.to;
                          return (
                            <button
                              key={preset.id}
                              className={active ? "is-active" : undefined}
                              type="button"
                              aria-pressed={active}
                              onClick={() => updateDraftFilter(fieldId, {
                                from: preset.from,
                                to: preset.to,
                              })}
                            >
                              {preset.label}
                            </button>
                          );
                        })}
                      </div>
                      <label>
                        <span>Desde</span>
                        <input
                          type="date"
                          value={filter.from}
                          min={fieldId === "system_created_at" && filter.to
                            ? shiftOneYear(filter.to, -1)
                            : undefined}
                          max={filter.to || undefined}
                          onChange={(event) => updateDraftFilter(fieldId, {
                            from: event.target.value,
                          })}
                        />
                      </label>
                      <label>
                        <span>Hasta</span>
                        <input
                          type="date"
                          value={filter.to}
                          min={filter.from || undefined}
                          max={fieldId === "system_created_at" && filter.from
                            ? shiftOneYear(filter.from, 1)
                            : undefined}
                          onChange={(event) => updateDraftFilter(fieldId, {
                            to: event.target.value,
                          })}
                        />
                      </label>
                      {fieldId === "system_created_at" && (
                        <small className="workflow-record-filter-range-note">
                          El rango máximo es de un año.
                        </small>
                      )}
                    </div>
                  )}

                  {filter.type === "select" && (
                    <div className="workflow-record-filter-options">
                      {(field.options || []).map((option) => (
                        <label key={option.id}>
                          <input
                            type="checkbox"
                            checked={filter.values.includes(option.id)}
                            onChange={(event) => updateDraftFilter(fieldId, {
                              values: event.target.checked
                                ? [...filter.values, option.id]
                                : filter.values.filter((id) => id !== option.id),
                            })}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {filterError && (
            <p className="workflow-record-filter-error">{filterError}</p>
          )}

          <footer>
            <button
              className="button secondary"
              type="button"
              disabled={Object.keys(draftFilters).length === 0
                && appliedFilterCount === 0}
              onClick={clearFilters}
            >
              Limpiar filtros
            </button>
            <button
              className="button primary"
              type="button"
              onClick={applyFilters}
            >
              Aplicar filtros
            </button>
          </footer>
        </section>
      )}

      {recordsLoading && records.length === 0 && (
        <div className="workflow-templates-loading">
          <LoaderCircle className="spin" size={20} />
          <span>Cargando registros…</span>
        </div>
      )}

      {!templateError && !recordsError && records.length > 0 && (
        <div
          className={recordsLoading
            ? "workflow-records-results is-loading"
            : "workflow-records-results"}
          aria-busy={recordsLoading}
        >
          <DataTable
            ariaLabel={`Registros de ${template.name}`}
            className="workflow-records-table"
            density="compact"
            minWidth={`${Math.max(
              620,
              template.fields.reduce(
                (total, field, index) => (
                  total + columnWidth(field, index, primaryFieldWidth)
                ),
                RECORD_ACTIONS_COLUMN_WIDTH,
              ),
            )}px`}
          >
            <colgroup>
              {template.fields.map((field, index) => (
                <col
                  key={field.id}
                  style={{
                    width: `${columnWidth(
                      field,
                      index,
                      primaryFieldWidth,
                    )}px`,
                  }}
                />
              ))}
              <col style={{ width: `${RECORD_ACTIONS_COLUMN_WIDTH}px` }} />
            </colgroup>
            <DataTableHead>
              <DataTableRow>
                {template.fields.map((field) => (
                  <DataTableHeaderCell key={field.id}>
                    <span
                      className="workflow-record-column-label"
                      title={field.label}
                    >
                      {field.label}
                    </span>
                  </DataTableHeaderCell>
                ))}
                <DataTableHeaderCell
                  align="end"
                  className="workflow-record-actions-header"
                >
                  <span className="sr-only">Acciones</span>
                </DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {records.map((record) => (
                <DataTableRow
                  key={record.id}
                  label="Ver registro"
                  onActivate={() => viewRecord(record.id)}
                >
                  {template.fields.map((field, index) => {
                    const displayValue = formatValue(
                      field,
                      record.values[field.id],
                    );
                    return (
                      <DataTableCell key={field.id}>
                        <span
                          className={index === 0
                            ? "workflow-record-primary-value"
                            : "workflow-record-value"}
                          title={displayValue === "—"
                            ? undefined
                            : displayValue}
                        >
                          {displayValue}
                        </span>
                      </DataTableCell>
                    );
                  })}
                  <DataTableCell
                    align="end"
                    className="workflow-record-actions-cell"
                  >
                    <WorkflowRecordActions
                      recordLabel={template.fields[0]
                        ? formatValue(
                            template.fields[0],
                            record.values[template.fields[0].id],
                          )
                        : "registro"}
                      onView={() => viewRecord(record.id)}
                      onEdit={() => editRecord(record.id)}
                      onDelete={() => {
                        setDeleteError("");
                        setDeleteTarget(record);
                      }}
                      onDownload={() => {
                        setDownloadError("");
                        setDownloadTarget(record);
                      }}
                    />
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>

          {pagination.pages > 1 && (
            <nav
              className="workflow-records-pagination"
              aria-label="Páginas de registros"
            >
              <span>
                {(pagination.page - 1) * pagination.limit + 1}–{
                  Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )
                } de {pagination.total} registros
              </span>
              <div>
                <button
                  className="button secondary"
                  type="button"
                  disabled={recordsLoading || pagination.page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  <ArrowLeft size={14} />
                  Anterior
                </button>
                <span>
                  Página {pagination.page} de {pagination.pages}
                </span>
                <button
                  className="button secondary"
                  type="button"
                  disabled={recordsLoading
                    || pagination.page >= pagination.pages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Siguiente
                  <ChevronRight size={14} />
                </button>
              </div>
            </nav>
          )}
        </div>
      )}

      {!templateError
        && !recordsError
        && !recordsLoading
        && records.length === 0 && (
        <EmptyState
          icon={Workflow}
          title={appliedFilterCount > 0
            ? "No encontramos registros"
            : "Todavía no hay registros"}
          description={appliedFilterCount > 0
            ? "Probá cambiando o quitando los filtros aplicados."
            : "Cargá el primero usando las columnas definidas en esta plantilla."}
          action={(
            <button
              className={appliedFilterCount > 0
                ? "button secondary"
                : "button primary"}
              type="button"
              onClick={appliedFilterCount > 0
                ? clearFilters
                : () => navigate(
                  `/flujos/${templateId}/registros/nuevo`,
                )}
            >
              {appliedFilterCount > 0
                ? <ListFilter size={17} />
                : <Plus size={17} />}
              {appliedFilterCount > 0
                ? "Limpiar filtros"
                : "Cargar primer registro"}
            </button>
          )}
        />
      )}

      {downloadTarget && (
        <WorkflowRecordDownloadDialog
          recordLabel={downloadTargetLabel}
          busyFormat={preparingFormat}
          error={downloadError}
          onDownloadWord={downloadRecordWord}
          onDownloadPdf={downloadRecordPdf}
          onViewText={viewRecordText}
          onClose={() => {
            if (!preparingFormat) setDownloadTarget(null);
          }}
        />
      )}

      {textPreview && (
        <WorkflowRecordTextDialog
          recordLabel={textPreview.recordLabel}
          text={textPreview.text}
          onClose={() => setTextPreview(null)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Eliminar registro"
          description={`“${deleteTargetLabel}” dejará de verse en este flujo. Los datos quedarán archivados para conservar la trazabilidad.`}
          confirmLabel={deleting ? "Eliminando…" : "Eliminar registro"}
          busy={deleting}
          onConfirm={deleteRecord}
          onClose={() => {
            if (!deleting) setDeleteTarget(null);
          }}
        />
      )}
    </div>
  );
}
